import './styles.css';
import './components/hex-map.js';
import './components/left-sidebar.js';
import './components/token-manager.js';
import './components/token-creator.js';
import './components/note-editor.js';
import './components/icon-picker.js';
import './components/print-modal.js';
import './components/calendar-view.js';
import { iconLibrary } from './utils/icon-library.js';
import {
  MONTH_NAMES,
  MONTH_LENGTHS,
  DEFAULT_GAME_DATE,
  formatGameDate,
  formatGameDateWithDays,
  calculateDaysElapsed,
  addDaysToDate
} from './utils/calendar.js';
import { openFileWithPicker, saveFileWithPicker } from './utils/file-utils.js';
import { toast } from './utils/toast.js';
import {
  showToast,
  showLoadingIndicator,
  hideLoadingIndicator,
  showSaveSuccess,
  showSessionLoaded
} from './utils/ui-helpers.js';
import { CONFIG } from './config.js';
import { HexEvents, createHexEvent } from './utils/hex-events.js';
import { setupDropdownToggle } from './utils/dropdown-toggle.js';
import {
  initializeEventSourcing,
  addEvent,
  undo,
  redo,
  canUndo,
  canRedo,
  rebuildState,
  findLastEventBeforeDate,
  EventType,
  migrateRevealHistoryToEvents,
  migrateIconsAndNotesToHaveIds
} from './utils/event-sourcing.js';
import { createDebuggedBroadcastChannel } from './utils/broadcast-debug.js';
import { sessionManager } from './utils/session-manager.js';
import { buildMapViewHTML, initializeMapComponent } from './utils/map-view-builder.js';
import {
  wireMapControls,
  wireHomeButton,
  wirePlayerViewButton,
  wireCalendarButton,
  wireDisplayIdToggle,
  wireRefreshMapButton,
  wireTimeTravelControls,
  wireDateDropdown,
  wireSaveUndoRedo,
  wirePrintButton,
  wireUtilitiesMenu,
  wireAutoSaveButton
} from './utils/map-view-wiring.js';
import { URLRouter } from './utils/url-router.js';
import { AutoSaveManager } from './utils/file-auto-save.js';
import {
  wireHexEvents,
  wireIconEvents,
  wireNoteEvents,
  wireTokenEvents,
  wireKeyboardShortcuts,
  wireToastNotifications
} from './utils/map-view-events.js';
import {
  setTimeTravelDay as setTimeTravelDayHelper,
  jumpToPresent as jumpToPresentHelper,
  populateBookmarkDropdown,
  wireBookmarkJump
} from './utils/time-travel-helpers.js';
import './components/debug-panel.js';
import './registerServiceWorker.js';

/**
 * ARCHITECTURE OVERVIEW
 * ===================
 *
 * This file (main.js) serves as the application entry point and coordinator.
 * Key responsibilities:
 * - Session management (create, load, save)
 * - View navigation (landing page → map view, player view)
 * - GM-Player sync via BroadcastChannel
 * - Time travel state management
 * - Calendar operations
 * - Event wiring for global UI elements
 *
 * Major sections (in order):
 * 1. Session state helpers (reveal history, token position)
 * 2. Player view sync (BroadcastChannel)
 * 3. Landing page event handlers (import, open, demo)
 * 4. Map view navigation and UI building
 * 5. Calendar view management
 * 6. Session file operations (save/load)
 * 7. Utility modals (tile sync, icon attribution)
 *
 * Components are defined in src/components/ as Web Components.
 * Utilities are in src/utils/ (hex-math, calendar, ui-helpers, etc.)
 */

// Application initialization

// Expose IconLibrary globally for testing
window.IconLibrary = iconLibrary;

// Re-export calendar utilities for backward compatibility
export {
  MONTH_NAMES,
  MONTH_LENGTHS,
  DEFAULT_GAME_DATE,
  formatGameDate,
  formatGameDateWithDays,
  calculateDaysElapsed,
  addDaysToDate
}

// DOM Element ID Constants
const ELEMENT_IDS = {
  MAIN_MAP: 'main-map',
  PLAYER_MAP: 'player-map',
  LEFT_SIDEBAR: 'left-sidebar',
  UNDO_BTN: 'undo-btn',
  REDO_BTN: 'redo-btn'
};

// Auto-save manager (saves to local file every 30 changes)
const autoSaveManager = new AutoSaveManager(30);

/**
 * Helper function to get hex map element based on view mode
 * @param {string} viewMode - Either 'gm' or 'player'
 * @returns {HTMLElement|null} The hex-map element
 */
function getHexMapElement(viewMode = 'gm') {
  const elementId = viewMode === 'player' ? ELEMENT_IDS.PLAYER_MAP : ELEMENT_IDS.MAIN_MAP;
  return document.getElementById(elementId);
}

/**
 * Helper function to get left sidebar element
 * @returns {HTMLElement|null} The left-sidebar element
 */
function getLeftSidebarElement() {
  return document.getElementById(ELEMENT_IDS.LEFT_SIDEBAR);
}

/**
 * Mark the current session as modified
 * Updates lastModified timestamp, saves to storage, and optionally updates undo/redo button states
 * @param {boolean} updateButtons - Whether to update undo/redo button states
 */
function markSessionModified(updateButtons = false) {
  sessionManager.markModified();

  // Save session state to storage (sessionStorage for demos, localStorage for normal sessions)
  const session = sessionManager.getSession();
  const mapData = sessionManager.getMapData();
  if (session && mapData) {
    URLRouter.saveSessionState(session, mapData);

    // Trigger auto-save to file (every 30 changes, non-blocking)
    autoSaveManager.recordChange(session, mapData).catch(err => {
      console.warn('[Auto-Save] Failed:', err);
    });
  }

  if (updateButtons) {
    updateUndoRedoButtons();
  }
}

/**
 * Check if the map is in historical (read-only) mode.
 * @returns {boolean} True if viewing historical state
 */
export function isHistoricalMode() {
  const timeTravelDay = window.currentSession?.timeTravelDay;
  const currentDaysElapsed = window.currentSession?.calendar?.daysElapsed || 1;
  return timeTravelDay !== null && timeTravelDay !== undefined && timeTravelDay < currentDaysElapsed;
}

/**
 * Get the current revealed state.
 * Event sourcing sets hexState.revealed directly via applyEvent().
 * Time travel is handled by rebuildState() at a specific eventId.
 * @param {Object} hexState - Hex state object
 * @returns {boolean} Current revealed state (or historical if time traveling)
 */
export function getCurrentRevealState(hexState) {
  // Event sourcing sets hexState.revealed directly via applyEvent()
  // Time travel is handled by rebuildState() at a specific eventId
  // IMPORTANT: Fallback to revealHistory for backward compatibility and synced states
  if (hexState.revealed !== undefined) {
    return hexState.revealed === true;
  }

  // Fallback: Check revealHistory if revealed property not set
  if (hexState.revealHistory && hexState.revealHistory.length > 0) {
    const lastRevealEvent = hexState.revealHistory[hexState.revealHistory.length - 1];
    return lastRevealEvent.revealed === true;
  }

  return false;
}

/**
 * Get the current position from positionHistory.
 * @param {Object} token - Token object
 * @returns {Object|null} Current position {col, row} or null if not placed
 */
export function getCurrentTokenPosition(token, timeTravelDay = null) {
  // Handle old tokens with position field instead of positionHistory
  if (token.position !== undefined && !token.positionHistory) {
    return token.position;
  }

  // New format: check last entry in positionHistory
  if (!token.positionHistory || token.positionHistory.length === 0) {
    return null; // Not placed yet
  }

  // If time traveling, filter position history by date
  if (timeTravelDay !== null) {
    const startDate = window.currentSession?.calendar?.campaignStartDate;
    if (startDate) {
      const validPositions = token.positionHistory.filter(entry => {
        if (!entry.gameDate) return true; // Include entries without dates for compatibility

        const entryDay = calculateDaysElapsed(startDate, entry.gameDate);
        return entryDay <= timeTravelDay;
      });

      if (validPositions.length === 0) {
        return null; // Token not yet placed at this historical point
      }

      return validPositions[validPositions.length - 1].position;
    }
  }

  return token.positionHistory[token.positionHistory.length - 1].position;
}

/**
 * Add a position change to the token's history.
 * @param {Object} token - Token object
 * @param {Object} position - Position object {col, row} or null
 */
export function addTokenPositionHistoryEntry(token, position) {
  // Ensure positionHistory exists
  if (!token.positionHistory) {
    token.positionHistory = [];
  }

  // Remove old position field if it exists (migration cleanup)
  if (token.position !== undefined) {
    delete token.position;
  }

  // Get game date from current session
  const gameDate = window.currentSession?.calendar?.currentDate
    ? { ...window.currentSession.calendar.currentDate }
    : DEFAULT_GAME_DATE;

  // Add entry to history
  token.positionHistory.push({
    position,
    gameDate,
    createdAt: Date.now()
  });
}

/**
 * Migrate old hex state format to new revealHistory format.
 * @param {Object} hexState - Hex state object
 */
function migrateHexStateToRevealHistory(hexState) {
  // If already has revealHistory, nothing to do
  if (hexState.revealHistory) return;

  // If has old boolean revealed field, migrate it
  if (hexState.revealed !== undefined) {
    const wasRevealed = hexState.revealed;
    delete hexState.revealed; // Remove old field

    // If it was revealed, add an entry to history
    if (wasRevealed) {
      hexState.revealHistory = [{
        revealed: true,
        gameDate: window.currentSession?.calendar?.startDate || DEFAULT_GAME_DATE,
        createdAt: Date.now()
      }];
    } else {
      hexState.revealHistory = [];
    }
  } else {
    // No revealed field at all, start with empty history
    hexState.revealHistory = [];
  }
}

/**
 * Ensures a hex state exists for the given coordinate.
 * Creates default state if missing.
 * @param {string} hexCoord - Hex coordinate string (e.g., "3,5")
 * @returns {Object} The hex state object
 */
function ensureHexState(hexCoord) {
  if (!window.currentSession?.hexStates) return null;

  if (!window.currentSession.hexStates[hexCoord]) {
    window.currentSession.hexStates[hexCoord] = {
      revealHistory: [],
      notes: [],
      icons: []
    };
  }

  // Ensure sub-arrays exist (for older sessions)
  const state = window.currentSession.hexStates[hexCoord];
  if (!state.notes) state.notes = [];
  if (!state.icons) state.icons = [];

  // Migrate old format if needed
  migrateHexStateToRevealHistory(state);

  return state;
}

// Detect view mode from URL
const urlState = URLRouter.getState();
const isPlayerView = urlState.view === 'player';

// Create BroadcastChannel for GM-Player sync (with debugging)
const gmChannel = createDebuggedBroadcastChannel(CONFIG.BROADCAST_CHANNEL_NAME);

// Listen for time travel updates from GM view
gmChannel.addEventListener('message', async (event) => {
  if (event.data.type === 'time-travel-update') {
    // Update time travel state without broadcasting (to avoid loops)
    await handleTimeTravelSync(event.data.day);
  } else if (event.data.type === 'session-state-update') {
    // Sync calendar state and map data from GM
    await handleSessionStateSync(event.data);
  } else if (event.data.type === 'request-session-state') {
    // Player View requesting current state (e.g., after refresh) - GM responds
    if (!isPlayerView && window.currentSession && window.currentMapData) {
      broadcastSessionState();
    }
  } else if (event.data.type === 'hex-selection') {
    // Sync hex selection from GM to Player View
    if (isPlayerView) {
      const hexClickedEvent = new CustomEvent('hex-clicked', {
        detail: event.data.hexData
      });
      document.dispatchEvent(hexClickedEvent);
    }
  } else if (event.data.type === 'hex-deselection') {
    // Sync hex deselection from GM to Player View
    if (isPlayerView) {
      const mapClickedEvent = new CustomEvent('map-clicked');
      document.dispatchEvent(mapClickedEvent);
    }
  } else if (event.data.type === 'undo') {
    // Sync undo from GM to Player View
    if (isPlayerView && window.currentSession) {
      window.currentSession.currentEventId = event.data.eventId;
      rebuildState(window.currentSession, event.data.eventId);
      const hexMap = document.getElementById('player-map');
      if (hexMap) {
        hexMap.session = window.currentSession;
      }
      await refreshMapDisplay('player-map');
    }
  } else if (event.data.type === 'redo') {
    // Sync redo from GM to Player View
    if (isPlayerView && window.currentSession) {
      window.currentSession.currentEventId = event.data.eventId;
      rebuildState(window.currentSession, event.data.eventId);
      const hexMap = document.getElementById('player-map');
      if (hexMap) {
        hexMap.session = window.currentSession;
      }
      await refreshMapDisplay('player-map');
    }
  }
});

// Clean up BroadcastChannel on page unload to prevent memory leaks
window.addEventListener('beforeunload', () => {
  gmChannel?.close();
});

/**
 * Refresh map display (fog of war, icons, and tokens)
 * @param {string} mapElementId - The ID of the hex-map element to refresh
 */
async function refreshMapDisplay(mapElementId) {
  const hexMap = document.getElementById(mapElementId);
  if (hexMap) {
    if (hexMap.refreshFogOfWar) await hexMap.refreshFogOfWar();
    if (hexMap.refreshIcons) hexMap.refreshIcons();
    if (hexMap.refreshTokens) hexMap.refreshTokens();
  }
}

/**
 * Handle session state sync from GM view (for Player View)
 */
async function handleSessionStateSync(event) {
  const { sessionState, mapData } = event;

  // If this is the first sync or map has changed, initialize/reload the map
  if (mapData && (!window.currentMapData || sessionState.mapName !== window.currentSession?.mapName)) {
    // Store the new map data and session
    window.currentMapData = mapData;
    window.currentSession = {
      ...sessionState,
      id: 'player-session'
      // formatVersion comes from sessionState (don't override it)
    };

    // Migrate old session formats to event sourcing (if needed)
    initializeEventSourcing(window.currentSession);
    migrateRevealHistoryToEvents(window.currentSession);
    migrateIconsAndNotesToHaveIds(window.currentSession);

    // Rebuild state from events to ensure consistency
    if (window.currentSession.history && window.currentSession.history.length > 0) {
      rebuildState(window.currentSession, window.currentSession.currentEventId);
    }

    // Reload the map with new data
    const hexMap = document.getElementById('player-map');
    if (hexMap) {
      await hexMap.loadMap(window.currentSession, mapData);

      // Update player view displays
      updatePlayerViewDateDisplay();

      // Auto-zoom to fit revealed tiles
      if (hexMap.resetView) {
        hexMap.resetView();
      }
    }

    // Refresh the sidebar
    const leftSidebar = document.getElementById('left-sidebar');
    if (leftSidebar && leftSidebar.render) {
      leftSidebar.render();
    }

    return;
  }

  // If no session exists yet, create one from the state
  if (!window.currentSession) {
    window.currentSession = {
      ...sessionState,
      id: 'player-session'
      // formatVersion comes from sessionState (don't override it)
    };

    // Migrate old session formats to event sourcing (if needed)
    initializeEventSourcing(window.currentSession);
    migrateRevealHistoryToEvents(window.currentSession);
    migrateIconsAndNotesToHaveIds(window.currentSession);

    // Rebuild state from events to ensure consistency
    if (window.currentSession.history && window.currentSession.history.length > 0) {
      rebuildState(window.currentSession, window.currentSession.currentEventId);
    }

    return;
  }

  // Sync calendar state from GM
  if (sessionState.calendar) {
    window.currentSession.calendar = sessionState.calendar;
  }

  // Sync hex states (revealed, notes, icons)
  if (sessionState.hexStates) {
    window.currentSession.hexStates = sessionState.hexStates;
  }

  // Sync tokens
  if (sessionState.tokens) {
    window.currentSession.tokens = sessionState.tokens;
  }

  // Sync map name
  if (sessionState.mapName) {
    window.currentSession.mapName = sessionState.mapName;
  }

  // Sync selected hex
  if (sessionState.selectedHex !== undefined) {
    window.currentSession.selectedHex = sessionState.selectedHex;
  }

  // Sync event sourcing fields (format v4)
  if (sessionState.history !== undefined) {
    window.currentSession.history = sessionState.history;
  }
  if (sessionState.currentEventId !== undefined) {
    window.currentSession.currentEventId = sessionState.currentEventId;
  }
  if (sessionState.timeTravelEventId !== undefined) {
    window.currentSession.timeTravelEventId = sessionState.timeTravelEventId;
  }
  if (sessionState.undoStack !== undefined) {
    window.currentSession.undoStack = sessionState.undoStack;
  }
  if (sessionState.redoStack !== undefined) {
    window.currentSession.redoStack = sessionState.redoStack;
  }

  // Update Player View date display
  updatePlayerViewDateDisplay();

  // Update hex-map component's session reference so it sees the new hexStates
  const hexMap = document.getElementById('player-map');
  if (hexMap) {
    hexMap.session = window.currentSession;

    // Refresh the map
    if (hexMap.refreshFogOfWar) await hexMap.refreshFogOfWar();
    if (hexMap.refreshIcons) hexMap.refreshIcons();
    if (hexMap.refreshTokens) hexMap.refreshTokens();

    // Auto-zoom to fit revealed tiles after sync
    if (hexMap.resetView) {
      hexMap.resetView();
    }

    // Sync selected hex state
    if (sessionState.selectedHex) {
      const hexClickedEvent = new CustomEvent('hex-clicked', {
        detail: sessionState.selectedHex
      });
      document.dispatchEvent(hexClickedEvent);
    } else if (sessionState.selectedHex === null) {
      // Explicitly deselect if GM has no selection
      const mapClickedEvent = new CustomEvent('map-clicked');
      document.dispatchEvent(mapClickedEvent);
    }
  }

  // Refresh the sidebar to update revealed count
  const leftSidebar = document.getElementById('left-sidebar');
  if (leftSidebar && leftSidebar.render) {
    leftSidebar.render();
  }
}

/**
 * Handle time travel sync from GM view (for Player View)
 */
async function handleTimeTravelSync(day) {
  if (!window.currentSession) return;

  const currentDaysElapsed = window.currentSession.calendar?.daysElapsed || 1;
  const isHistorical = day < currentDaysElapsed;

  // Store the time travel day in the session
  window.currentSession.timeTravelDay = isHistorical ? day : null;

  // Update the Player View banner to show historical state
  updatePlayerViewHistoricalBanner(isHistorical, day);

  // Refresh the map to show historical state
  await refreshMapDisplay('player-map');
}

/**
 * Broadcast session state to Player View (from GM view)
 */
function broadcastSessionState() {
  if (!window.currentSession || isPlayerView) return;

  // Send session state update (including map info for Player View initialization)
  gmChannel.postMessage({
    type: 'session-state-update',
    sessionState: {
      mapName: window.currentSession.mapName,
      mapFile: window.currentSession.mapFile,
      formatVersion: window.currentSession.formatVersion,
      calendar: window.currentSession.calendar,
      hexStates: window.currentSession.hexStates,
      tokens: window.currentSession.tokens,
      selectedHex: window.currentSession.selectedHex,
      // Event sourcing fields (format v4)
      history: window.currentSession.history,
      currentEventId: window.currentSession.currentEventId,
      timeTravelEventId: window.currentSession.timeTravelEventId,
      undoStack: window.currentSession.undoStack,
      redoStack: window.currentSession.redoStack
    },
    mapData: window.currentMapData // Include map data for initial load
  });
}

/**
 * Update Player View banner to show historical state
 */
function updatePlayerViewHistoricalBanner(isHistorical, day) {
  const playerHeader = document.querySelector('.app-header .header-left');
  if (!playerHeader) return;

  let historicalBanner = document.getElementById('player-historical-banner');

  if (isHistorical) {
    // Calculate the date for this day
    const startDate = window.currentSession.calendar?.startDate || DEFAULT_GAME_DATE;
    const historicalDate = addDaysToDate(startDate, day - 1);
    const dateStr = formatGameDate(historicalDate);

    if (!historicalBanner) {
      // Create the banner
      historicalBanner = document.createElement('div');
      historicalBanner.id = 'player-historical-banner';
      historicalBanner.style.cssText = `
        background-color: ${CONFIG.COLORS.WARNING};
        color: ${CONFIG.COLORS.TEXT_BLACK};
        padding: 8px 16px;
        border-radius: 4px;
        margin-left: 16px;
        font-size: 16px;
        font-weight: 700;
        border: 2px solid ${CONFIG.COLORS.WARNING_BORDER};
        box-shadow: 0 2px 8px ${CONFIG.COLORS.WARNING_SHADOW};
        animation: pulse-warning 2s ease-in-out infinite;
      `;
      playerHeader.appendChild(historicalBanner);
    }

    historicalBanner.textContent = `Viewing Campaign History: ${dateStr}`;
    historicalBanner.style.display = 'block';
  } else {
    // Remove the banner when returning to present
    if (historicalBanner) {
      historicalBanner.style.display = 'none';
    }
  }
}

// Wire up landing page buttons
document.addEventListener('DOMContentLoaded', async () => {
  // Check if we should auto-load a session from URL
  const urlState = URLRouter.getState();

  // Player view: auto-load from sync or stored session
  if (isPlayerView) {
    await initPlayerView();
    return;
  }

  // GM view: check for session in URL and auto-restore
  if (urlState.session) {
    if (urlState.session === 'demo') {
      // Try to restore demo session from sessionStorage first
      const storedState = URLRouter.loadSessionState('demo');
      if (storedState && storedState.session && storedState.mapData) {
        try {
          showLoadingIndicator('Restoring demo session...');

          const { session, mapData } = storedState;

          // Migrate and initialize
          initializeEventSourcing(session);
          migrateRevealHistoryToEvents(session);
          migrateIconsAndNotesToHaveIds(session);

          // Rebuild state from events
          if (session.history && session.history.length > 0) {
            rebuildState(session, session.currentEventId);
          }

          // Store in session manager
          sessionManager.setSession(session);
          sessionManager.setMapData(mapData);

          // Navigate to map view
          await navigateToMapView(session);

          // Broadcast to player view
          broadcastSessionState();

          hideLoadingIndicator();
          return;
        } catch (error) {
          console.error('Failed to restore demo session:', error);
          hideLoadingIndicator();
          toast.error('Failed to restore demo. Loading fresh demo.');
          // Clear invalid demo data
          URLRouter.clearSessionState('demo');
          // Fall through to load fresh demo
        }
      }

      // No saved demo or failed to restore - load fresh demo
      await handleLoadDemo();
      return;
    } else {
      // Try to restore session from localStorage
      const storedState = URLRouter.loadSessionState();
      if (storedState && storedState.session && storedState.mapData) {
        try {
          showLoadingIndicator('Restoring session...');

          const { session, mapData } = storedState;

          // Migrate and initialize
          initializeEventSourcing(session);
          migrateRevealHistoryToEvents(session);
          migrateIconsAndNotesToHaveIds(session);

          // Rebuild state from events
          if (session.history && session.history.length > 0) {
            rebuildState(session, session.currentEventId);
          }

          // Store in session manager
          sessionManager.setSession(session);
          sessionManager.setMapData(mapData);

          // Navigate to map view
          await navigateToMapView(session);

          // Broadcast to player view
          broadcastSessionState();

          hideLoadingIndicator();
          return;
        } catch (error) {
          console.error('Failed to restore session:', error);
          hideLoadingIndicator();
          toast.error('Failed to restore session. Starting fresh.');
          // Clear invalid session data
          URLRouter.clearSessionState();
          URLRouter.setState({ session: null });
        }
      }
    }
  }

  // Show landing page
  const importMapBtn = document.getElementById('import-map-btn');
  const openSessionBtn = document.getElementById('open-session-btn');

  if (importMapBtn) {
    importMapBtn.addEventListener('click', handleImportMap);
  }

  if (openSessionBtn) {
    openSessionBtn.addEventListener('click', handleOpenSession);
  }

  const loadDemoBtn = document.getElementById('load-demo-btn');
  if (loadDemoBtn) {
    loadDemoBtn.addEventListener('click', handleLoadDemo);
  }
});

async function handleImportMap() {
  try {
    const jsonFile = await openFileWithPicker({
      types: [
        {
          description: 'Hexton Hills Map JSON',
          accept: {
            'application/json': ['.json']
          }
        }
      ]
    });

    if (!jsonFile.name.endsWith('.json')) {
      toast.error('Please select a JSON file');
      return;
    }

    await processMapImport(jsonFile);
  } catch (error) {
    if (error.name !== 'AbortError' && error.message !== 'File selection cancelled') {
      console.error('Error importing map:', error);
      toast.error('Failed to import map: ' + error.message);
    }
  }
}

async function processMapImport(jsonFile) {
  try {
    // Show loading indicator
    showLoadingIndicator('Importing map...');

    // Read and validate JSON
    const jsonText = await jsonFile.text();
    const mapData = JSON.parse(jsonText);

    // Validate Hexton Hills format
    if (!mapData.version || mapData.version !== 4) {
      throw new Error('Invalid map file: Not a Hexton Hills version 4 export');
    }

    if (!mapData.bounds || !mapData.tiles) {
      throw new Error('Invalid map file: Missing required data (bounds or tiles)');
    }

    // Check if tiles are synced locally
    const tilesAvailable = await checkLocalTiles(mapData.tiles);
    if (!tilesAvailable) {
      hideLoadingIndicator();
      toast.error('Tiles not found locally. Please run "npm run update-tiles" to download missing tiles, then commit them to the repository.', 5000);
      return;
    }

    // Create session with the imported map (no image ID needed - tiles are local)
    const session = await createNewSession(mapData, jsonFile.name.replace('.json', ''));

    // Update URL with session ID
    URLRouter.setState({ session: session.id, view: 'gm' });

    // Navigate to map view
    await navigateToMapView(session);

    // Broadcast session state to Player View
    broadcastSessionState();

    hideLoadingIndicator();
  } catch (error) {
    hideLoadingIndicator();
    console.error('Error processing map import:', error);
    toast.error(error.message);
  }
}

async function checkLocalTiles(tiles) {
  // Check if at least the first tile is available locally
  if (!tiles || tiles.length === 0) return false;

  const firstTile = tiles[0];
  const tileUrl = `/content/tiles/${firstTile.tileId}.png`;

  try {
    const response = await fetch(tileUrl, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
}

async function createNewSession(mapData, mapName, mapFilePath = null, isDemo = false) {
  return sessionManager.createNewSession(mapData, mapName, mapFilePath, isDemo);
}

async function navigateToMapView(session) {
  // Save session state to localStorage for URL restoration
  URLRouter.saveSessionState(session, sessionManager.getMapData());

  // Build and insert map view HTML
  document.body.innerHTML = buildMapViewHTML(session);

  // Initialize the hex-map component
  const mapData = sessionManager.getMapData();

  if (!mapData) {
    throw new Error('Cannot navigate to map view: mapData is null in sessionManager');
  }

  const hexMap = await initializeMapComponent(session, mapData);

  // Create wrappers for time travel helpers with proper context
  const setTimeTravelDay = (day, broadcast = true) =>
    setTimeTravelDayHelper(day, broadcast, gmChannel, isPlayerView);

  const jumpToPresent = jumpToPresentHelper;

  // Store setTimeTravelDay in window for jumpToPresent to access
  window.__setTimeTravelDay__ = setTimeTravelDay;

  // Wire up UI controls
  wireMapControls(hexMap);
  wireHomeButton();
  wirePlayerViewButton(broadcastSessionState);
  wireCalendarButton(showCalendarView);
  wireRefreshMapButton(hexMap);
  wireTimeTravelControls({ session, setTimeTravelDay, jumpToPresent, populateBookmarkDropdown });

  // Wire up bookmark jump handler
  wireBookmarkJump(setTimeTravelDay);

  wireDateDropdown(advanceTime, showCalendarView);

  wireSaveUndoRedo({ handleSaveSession, handleUndo, handleRedo });

  // Wire up auto-save button (only available in Chromium browsers + non-demo sessions)
  wireAutoSaveButton({
    autoSaveManager,
    getSession: () => sessionManager.getSession(),
    getMapData: () => sessionManager.getMapData()
  });

  // Initialize undo/redo button states
  updateUndoRedoButtons();

  // Wire up Display ID toggle button
  const toggleIdsBtn = document.getElementById('toggle-ids-btn');
  if (toggleIdsBtn && hexMap) {
    // Initialize toggle state from session or default to hidden
    const showIds = window.currentSession?.showDisplayIds ?? false;
    hexMap.setDisplayIdsVisible(showIds);
    updateToggleIdsButton(toggleIdsBtn, showIds);

    toggleIdsBtn.addEventListener('click', () => {
      const newState = hexMap.toggleDisplayIds();
      // Persist state in session
      if (window.currentSession) {
        window.currentSession.showDisplayIds = newState;
      }
      updateToggleIdsButton(toggleIdsBtn, newState);
    });
  }

  // Wire up print button
  const printBtn = document.getElementById('print-btn');
  const printModal = document.getElementById('print-modal');
  if (printBtn && printModal) {
    printBtn.addEventListener('click', () => {
      printModal.open();
    });

    // Handle print mode selection
    printModal.addEventListener('print-mode-selected', async (event) => {
      const { mode } = event.detail;
      const hexMap = document.getElementById('main-map');

      try {
        const dataUrl = await hexMap.exportForPrint(mode);

        // Open print window
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          toast.error('Please allow popups to print the map');
          printModal.close();
          return;
        }

        const modeLabel = mode === 'gm' ? 'GM' : 'Player';
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${session.mapName} - ${modeLabel} Map</title>
              <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body {
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  min-height: 100vh;
                  background: white;
                  padding: 0.5in;
                }
                .print-header {
                  padding: 20px;
                  text-align: center;
                  background: #f0f0f0;
                  border-bottom: 2px solid #333;
                  position: sticky;
                  top: 0;
                  z-index: 100;
                  width: 100%;
                }
                img {
                  max-width: 100%;
                  height: auto;
                  display: block;
                  margin: 20px auto;
                }
                @media print {
                  body {
                    padding: 0;
                    display: block;
                  }
                  .print-header {
                    display: none !important;
                  }
                  img {
                    margin: 0;
                    max-width: 100%;
                    width: 100%;
                    height: auto;
                    page-break-inside: avoid;
                  }
                  @page {
                    margin: 0.5in;
                  }
                }
              </style>
            </head>
            <body>
              <div class="print-header">
                <p style="margin: 0; font-size: 16px;">
                  <strong>📄 Press Ctrl+P (Windows/Linux) or Cmd+P (Mac) to print this map</strong>
                </p>
              </div>
              <img src="${dataUrl}" alt="${modeLabel} Map" />
            </body>
          </html>
        `);
        printWindow.document.close();

        printModal.close();
      } catch (error) {
        console.error('Print error:', error);
        toast.error('Failed to generate print: ' + error.message);
        printModal.close();
      }
    });
  }

  wireUtilitiesMenu({ syncTiles: triggerTileSync, showIconAttribution: showIconAttributionModal });

  // Wire up keyboard shortcuts
  wireKeyboardShortcuts({ handleSaveSession, handleUndo, handleRedo, isPlayerView });

  // Wire up hex events
  wireHexEvents({ isPlayerView, gmChannel, markSessionModified, broadcastSessionState });

  // Wire up icon events
  wireIconEvents({ markSessionModified, broadcastSessionState, ensureHexState });

  // Wire up note events
  wireNoteEvents({ markSessionModified, broadcastSessionState, ensureHexState });

  // Wire up token events
  wireTokenEvents({ markSessionModified, broadcastSessionState, getHexMapElement });

  // Wire up toast notifications
  wireToastNotifications(showToast);
}

/**
 * Show Calendar View
 * Replaces the map view with the calendar management interface
 */
function showCalendarView() {
  const session = window.currentSession;
  if (!session) return;

  // Hide map and sidebar
  const mainContainer = document.querySelector('.app-container main');
  const mapElement = document.getElementById('main-map');
  const sidebarElement = document.getElementById('left-sidebar');

  if (mapElement) mapElement.style.display = 'none';
  if (sidebarElement) sidebarElement.style.display = 'none';

  // Create or show calendar view
  let calendarView = document.getElementById('calendar-view');
  if (!calendarView) {
    calendarView = document.createElement('calendar-view');
    calendarView.id = 'calendar-view';
    mainContainer.appendChild(calendarView);
  }

  calendarView.style.display = 'block';
  calendarView.setSession(session);

  // Listen for back to map event
  calendarView.addEventListener('back-to-map', () => {
    showMapView();
  });

  // Listen for calendar changes
  calendarView.addEventListener('calendar-changed', (event) => {
    // Update the header date display
    updateHeaderDateDisplay();

    // Mark session as modified
    markSessionModified();

    // Broadcast session state to Player View
    broadcastSessionState();
  });
}

/**
 * Show Map View
 * Returns from calendar view to the map view
 */
function showMapView() {
  const mapElement = document.getElementById('main-map');
  const sidebarElement = document.getElementById('left-sidebar');
  const calendarView = document.getElementById('calendar-view');

  if (mapElement) mapElement.style.display = 'block';
  if (sidebarElement) sidebarElement.style.display = 'block';
  if (calendarView) calendarView.style.display = 'none';
}

/**
 * Update Header Date Display
 * Updates the current date shown in the header
 */
function updateHeaderDateDisplay() {
  const session = window.currentSession;
  if (!session || !session.calendar) return;

  const currentDateText = formatGameDateWithDays(
    session.calendar.currentDate,
    session.calendar.daysElapsed
  );

  const dateDisplay = document.getElementById('current-date-display');
  if (dateDisplay) {
    dateDisplay.textContent = currentDateText;
  }
}

/**
 * Update Player View date display
 */
function updatePlayerViewDateDisplay() {
  const session = window.currentSession;
  if (!session || !session.calendar) return;

  // Update map name in header
  const mapNameEl = document.getElementById('player-view-map-name');
  if (mapNameEl) {
    mapNameEl.textContent = `🗺️ ${session.mapName}`;
  }

  // Format date with [PLAYER VIEW] suffix
  const currentDateText = formatGameDateWithDays(
    session.calendar.currentDate,
    session.calendar.daysElapsed
  );

  const dateDisplay = document.getElementById('player-view-date-display');
  if (dateDisplay) {
    dateDisplay.textContent = `${currentDateText} [PLAYER VIEW]`;
  }
}

/**
 * Advance Time
 * Advances the current date by the specified number of days
 */
function advanceTime(daysToAdvance) {
  const session = window.currentSession;
  if (!session || !session.calendar) return;

  // Add days to current date
  const current = session.calendar.currentDate;
  const newDate = addDaysToDate(current, daysToAdvance);

  // Update session
  session.calendar.currentDate = newDate;

  // Recalculate days elapsed
  session.calendar.daysElapsed = calculateDaysElapsed(
    session.calendar.campaignStartDate,
    session.calendar.currentDate
  );

  // Update header display
  updateHeaderDateDisplay();

  // Update time travel slider max value
  const timeSlider = document.getElementById('time-slider');
  if (timeSlider) {
    timeSlider.max = session.calendar.daysElapsed;
    timeSlider.value = session.calendar.daysElapsed;

    const timeDisplay = document.getElementById('time-display');
    if (timeDisplay) {
      timeDisplay.textContent = `Day ${session.calendar.daysElapsed}`;
    }

    // Reset time travel mode when advancing time
    session.timeTravelDay = null;
    const historicalIndicator = document.getElementById('historical-indicator');
    if (historicalIndicator) {
      historicalIndicator.style.display = 'none';
    }
  }

  // Mark session as modified
  markSessionModified();

  // Broadcast session state to Player View
  broadcastSessionState();
}

// Loading indicator functions now imported from ui-helpers.js

/**
 * Update undo/redo button states
 */
function updateUndoRedoButtons() {
  const undoBtn = document.getElementById('undo-btn');
  const redoBtn = document.getElementById('redo-btn');

  if (undoBtn && window.currentSession) {
    undoBtn.disabled = !canUndo(window.currentSession);
  }

  if (redoBtn && window.currentSession) {
    redoBtn.disabled = !canRedo(window.currentSession);
  }
}

/**
 * Handle undo action
 */
async function handleUndo() {
  console.log('[UNDO] handleUndo CALLED');
  const session = sessionManager.getSession();
  if (!session) {
    console.log('[UNDO] No session, exiting');
    return;
  }

  console.log('[UNDO] Before undo - currentEventId:', session.currentEventId, 'undoStack:', session.undoStack);

  if (undo(session)) {
    console.log('[UNDO] After undo - currentEventId:', session.currentEventId);
    console.log('[UNDO] Sample hexState:', Object.values(session.hexStates)[0]);

    // Update hex-map component's session reference
    const hexMap = document.getElementById('main-map');
    if (hexMap) {
      hexMap.session = session;
      console.log('[UNDO] Updated hexMap.session');
    }

    // Refresh the map
    await refreshMapDisplay('main-map');

    // Update sidebar
    const leftSidebar = document.getElementById('left-sidebar');
    if (leftSidebar && leftSidebar.render) {
      leftSidebar.render();
    }

    // Update button states
    updateUndoRedoButtons();

    // Broadcast undo to Player View
    if (!isPlayerView) {
      gmChannel.postMessage({
        type: 'undo',
        eventId: window.currentSession.currentEventId
      });
    }

    // Show notification
    toast.info('Undone');
  }
}

/**
 * Handle redo action
 */
async function handleRedo() {
  if (!window.currentSession) return;

  if (redo(window.currentSession)) {
    // Update hex-map component's session reference
    const hexMap = document.getElementById('main-map');
    if (hexMap) {
      hexMap.session = window.currentSession;
    }

    // Refresh the map
    await refreshMapDisplay('main-map');

    // Update sidebar
    const leftSidebar = document.getElementById('left-sidebar');
    if (leftSidebar && leftSidebar.render) {
      leftSidebar.render();
    }

    // Update button states
    updateUndoRedoButtons();

    // Broadcast redo to Player View
    if (!isPlayerView) {
      gmChannel.postMessage({
        type: 'redo',
        eventId: window.currentSession.currentEventId
      });
    }

    // Show notification
    toast.info('Redone');
  }
}

/**
 * Handle saving the current session to a JSON file
 */
async function handleSaveSession() {
  if (!window.currentSession) {
    toast.warning('No session to save. Please load a map first.');
    return;
  }

  try {
    // Update lastModified timestamp
    markSessionModified();

    // Format v2: Save session WITHOUT embedded mapData
    // Instead, session contains mapFile path reference
    const saveData = {
      ...window.currentSession
    };

    const jsonString = JSON.stringify(saveData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const defaultFilename = `${window.currentSession.mapName}-session.json`;

    await saveFileWithPicker(blob, defaultFilename, {
      description: 'Session JSON',
      accept: {
        'application/json': ['.json']
      }
    });

    // Show success feedback
    showSaveSuccess();
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('Error saving session:', error);
      toast.error('Failed to save session: ' + error.message);
    }
  }
}

/**
 * Update the Display ID toggle button visual state
 * @param {HTMLElement} btn - The toggle button element
 * @param {boolean} isActive - Whether display IDs are visible
 */
function updateToggleIdsButton(btn, isActive) {
  if (isActive) {
    btn.classList.add('btn-active');
    btn.title = 'Hide Display IDs';
  } else {
    btn.classList.remove('btn-active');
    btn.title = 'Show Display IDs';
  }
}

// Save success notification now imported from ui-helpers.js

async function handleOpenSession() {
  try {
    const file = await openFileWithPicker({
      types: [
        {
          description: 'Session JSON',
          accept: {
            'application/json': ['.json']
          }
        }
      ]
    });

    const jsonText = await file.text();
    const sessionData = JSON.parse(jsonText);

    // Validate session format
    if (!sessionData.formatVersion || !sessionData.mapName || !sessionData.hexStates) {
      throw new Error('Invalid session file: Missing required fields');
    }

    // Load map data based on format version
    let mapData;
    if (sessionData.formatVersion === 2 || sessionData.formatVersion === 3 || sessionData.formatVersion === 4) {
      // Format v2/v3/v4: Use mapFile reference
      if (!sessionData.mapFile) {
        throw new Error(`Invalid session file: Format v${sessionData.formatVersion} requires mapFile field`);
      }
      const response = await fetch(`/${sessionData.mapFile}`);
      if (!response.ok) {
        throw new Error(`Could not load map file: ${sessionData.mapFile}`);
      }
      mapData = await response.json();

      // For v2 sessions, add default calendar data (v3/v4 should already have it)
      if (sessionData.formatVersion === 2 && !sessionData.calendar) {
        sessionData.calendar = {
          campaignStartDate: DEFAULT_GAME_DATE,
          currentDate: DEFAULT_GAME_DATE,
          daysElapsed: 1,
          events: []
        };
      }
    } else {
      // Format v1: Check if mapData is embedded or needs to be loaded
      mapData = sessionData.mapData;
      if (!mapData) {
        // Try to load the map from campaigns folder (fallback)
        const response = await fetch(`/campaigns/${sessionData.mapName}/${sessionData.mapName}.json`);
        if (!response.ok) {
          throw new Error(`Could not find map data for "${sessionData.mapName}". The session file may be missing map data.`);
        }
        mapData = await response.json();
      }
      // For v1 sessions, add default calendar data
      sessionData.calendar = {
        campaignStartDate: DEFAULT_GAME_DATE,
        currentDate: DEFAULT_GAME_DATE,
        daysElapsed: 1,
        events: []
      };
    }

    // Validate mapData
    if (!mapData) {
      throw new Error('Map data is null after loading. This is a bug.');
    }
    if (!mapData.tiles) {
      throw new Error('Map data is missing tiles array. Invalid map file format.');
    }

    // Check if tiles are available
    const tilesAvailable = await checkLocalTiles(mapData.tiles);
    if (!tilesAvailable) {
      toast.error('Tiles not found locally. Please run "npm run update-tiles" to download missing tiles, then commit them to the repository.', 5000);
      return;
    }

    // Remove mapData from session before storing (it's stored separately)
    const { mapData: _, ...session } = sessionData;

    // Migrate old session formats to event sourcing
    initializeEventSourcing(session);
    migrateRevealHistoryToEvents(session);
    migrateIconsAndNotesToHaveIds(session);

    // Rebuild state from events to ensure consistency
    if (session.history && session.history.length > 0) {
      rebuildState(session, session.currentEventId);
    }

    // Store session and map data using sessionManager
    sessionManager.setSession(session);
    sessionManager.setMapData(mapData);

    // Update URL with session ID
    URLRouter.setState({ session: session.id, view: 'gm' });

    // Navigate to map view
    showLoadingIndicator('Loading session...');
    await navigateToMapView(session);

    // Broadcast session state to Player View
    broadcastSessionState();

    hideLoadingIndicator();

    // Show success feedback
    showSessionLoaded();

  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('Error loading session:', error);
      toast.error('Failed to load session: ' + error.message);
    }
  }
}

async function handleLoadDemo() {
  try {
    showLoadingIndicator('Loading castle demo...');

    // Fetch the demo map JSON (castle map)
    const response = await fetch('/campaigns/Mournstead/Mournstead-castle.json');
    if (!response.ok) {
      throw new Error('Demo map not found. Make sure the campaigns folder exists.');
    }

    const mapData = await response.json();

    // Validate Hexton Hills format
    if (!mapData.version || mapData.version !== 4) {
      throw new Error('Invalid map file: Not a Hexton Hills version 4 export');
    }

    // Check if tiles are available
    const tilesAvailable = await checkLocalTiles(mapData.tiles);
    if (!tilesAvailable) {
      hideLoadingIndicator();
      toast.error('Tiles not found locally. Please run "npm run update-tiles" to download missing tiles, then commit them to the repository.', 5000);
      return;
    }

    // Create demo session (isDemo: true)
    const session = await createNewSession(mapData, 'Mournstead Castle', 'campaigns/Mournstead/Mournstead-castle.json', true);

    // Update URL to indicate demo session
    URLRouter.setState({ session: 'demo', view: 'gm' });

    await navigateToMapView(session);

    // Broadcast session state to Player View
    broadcastSessionState();

    hideLoadingIndicator();
  } catch (error) {
    hideLoadingIndicator();
    console.error('Error loading demo:', error);
    toast.error('Failed to load demo: ' + error.message);
  }
}

/**
 * Initialize player view - fullscreen map with only revealed tiles
 */
async function initPlayerView() {
  try {
    // Create player view layout with sidebar
    document.body.innerHTML = `
      <div class="app-container">
        <header class="app-header">
          <div class="header-left">
            <button id="player-home-btn" class="btn btn-icon" title="Home" style="margin-right: 1rem;">
              <i class="fas fa-home"></i>
            </button>
            <h1 id="player-view-map-name">🗺️ Player View</h1>
            <div id="player-view-date-display" class="player-view-date-display"></div>
          </div>
          <div class="header-right">
            <button id="player-sync-btn" class="btn btn-icon" title="Sync with GM (refresh map, icons, notes)">
              <i class="fas fa-sync"></i>
            </button>
            <div class="player-status connected">
              <i class="fas fa-link"></i> Connected
            </div>
          </div>
        </header>

        <main class="main-content">
          <left-sidebar id="left-sidebar" view-mode="player"></left-sidebar>

          <div class="map-container">
            <hex-map id="player-map" view-mode="player"></hex-map>
            <div class="map-controls">
              <button class="btn btn-icon" title="Zoom In">
                <i class="fas fa-search-plus"></i>
              </button>
              <button class="btn btn-icon" title="Zoom Out">
                <i class="fas fa-search-minus"></i>
              </button>
              <button class="btn btn-icon" title="Reset View">
                <i class="fas fa-compress"></i>
              </button>
            </div>
          </div>
        </main>
        <debug-panel></debug-panel>
      </div>
      <style>
        .player-status {
          background: ${CONFIG.COLORS.SUCCESS_TINT};
          color: ${CONFIG.COLORS.SUCCESS};
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .player-view-date-display {
          font-size: 0.875rem;
          color: ${CONFIG.COLORS.TEXT_SECONDARY};
          margin-top: 0.25rem;
        }
      </style>
    `;

    // Player View will receive map data from GM via BroadcastChannel
    // Request current state from GM (in case Player View was refreshed or opened independently)
    gmChannel.postMessage({ type: 'request-session-state' });

    // Wire up map controls
    const zoomInBtn = document.querySelector('.map-controls button[title="Zoom In"]');
    const zoomOutBtn = document.querySelector('.map-controls button[title="Zoom Out"]');
    const resetBtn = document.querySelector('.map-controls button[title="Reset View"]');

    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', () => {
        if (hexMap && hexMap.zoom) {
          hexMap.zoom(CONFIG.ZOOM.IN_FACTOR);
        }
      });
    }

    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', () => {
        if (hexMap && hexMap.zoom) {
          hexMap.zoom(CONFIG.ZOOM.OUT_FACTOR);
        }
      });
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        if (hexMap && hexMap.resetView) {
          hexMap.resetView();
        }
      });
    }

    // Wire up home button to return to landing page
    const homeBtn = document.getElementById('player-home-btn');
    if (homeBtn) {
      homeBtn.addEventListener('click', () => {
        window.location.href = '/';
      });
    }

    // Wire up sync button to request fresh state from GM
    const syncBtn = document.getElementById('player-sync-btn');
    if (syncBtn) {
      syncBtn.addEventListener('click', () => {
        // Add spinning animation
        const icon = syncBtn.querySelector('i');
        icon.classList.add('fa-spin');

        // Request fresh state from GM
        gmChannel.postMessage({ type: 'request-session-state' });

        // Remove spin after a short delay (visual feedback)
        setTimeout(() => {
          icon.classList.remove('fa-spin');
        }, 1000);
      });
    }

    // Refresh sidebar after map loads to show map overview
    const sidebar = document.getElementById('left-sidebar');
    if (sidebar && sidebar.render) {
      sidebar.render();
    }

    // Update date display in Player View header
    updatePlayerViewDateDisplay();

    // Listen for hex-clicked events from BroadcastChannel to update sidebar
    document.addEventListener('hex-clicked', (event) => {
      const leftSidebar = document.getElementById('left-sidebar');
      if (leftSidebar && leftSidebar.selectHex) {
        // Only show hex if it's revealed
        const isRevealed = event.detail.state ? getCurrentRevealState(event.detail.state) : false;
        if (isRevealed) {
          leftSidebar.selectHex(event.detail);
        } else {
          // Hex is unrevealed - show overview instead
          leftSidebar.deselectHex();
        }
      }
    });

    // Listen for map-clicked (deselection) events
    document.addEventListener('map-clicked', () => {
      const leftSidebar = document.getElementById('left-sidebar');
      if (leftSidebar && leftSidebar.deselectHex) {
        leftSidebar.deselectHex();
      }
    });

  } catch (error) {
    console.error('Error initializing player view:', error);
    document.body.innerHTML = `
      <div class="waiting-message">
        <h2>⏳ Waiting for GM</h2>
        <p>The GM needs to load a map first.</p>
        <p style="margin-top: 1rem; font-size: 0.875rem; color: ${CONFIG.COLORS.TEXT_MUTED};">
          Error: ${error.message}
        </p>
      </div>
    `;
  }
}

/**
 * Trigger tile sync via API and show progress modal
 */
async function triggerTileSync() {
  // Create progress modal
  const modal = document.createElement('div');
  modal.className = 'sync-progress-modal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${CONFIG.COLORS.OVERLAY_DARKER};
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: ${CONFIG.Z_INDEX_TOAST};
  `;

  modal.innerHTML = `
    <div style="
      background: var(--bg-surface);
      border: 1px solid var(--border-default);
      border-radius: var(--radius-lg);
      padding: var(--space-xl);
      min-width: 500px;
      max-width: 600px;
      max-height: 80vh;
      overflow-y: auto;
    ">
      <h2 style="margin-bottom: var(--space-md); color: var(--text-primary);">Syncing Tiles</h2>
      <div id="sync-status" style="color: var(--text-secondary); margin-bottom: var(--space-md);">
        Starting tile sync...
      </div>
      <div id="sync-output" style="
        background: var(--bg-primary);
        border: 1px solid var(--border-subtle);
        border-radius: var(--radius-sm);
        padding: var(--space-md);
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        color: var(--text-secondary);
        max-height: 300px;
        overflow-y: auto;
        white-space: pre-wrap;
        word-break: break-word;
      "></div>
      <button id="sync-close-btn" class="btn btn-primary" style="margin-top: var(--space-lg); width: 100%; display: none;">
        Close
      </button>
    </div>
  `;

  document.body.appendChild(modal);

  const statusDiv = modal.querySelector('#sync-status');
  const outputDiv = modal.querySelector('#sync-output');
  const closeBtn = modal.querySelector('#sync-close-btn');

  closeBtn.addEventListener('click', () => modal.remove());

  try {
    const response = await fetch('/api/sync-tiles', {
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // Keep incomplete line in buffer

      for (const line of lines) {
        if (!line.trim()) continue;

        try {
          const data = JSON.parse(line);

          if (data.status === 'progress') {
            outputDiv.textContent += data.message + '\n';
            outputDiv.scrollTop = outputDiv.scrollHeight;
          } else if (data.status === 'complete') {
            statusDiv.textContent = '✅ ' + data.message;
            statusDiv.style.color = 'var(--color-success)';
            closeBtn.style.display = 'block';
          } else if (data.status === 'failed' || data.status === 'error') {
            statusDiv.textContent = '❌ ' + data.message;
            statusDiv.style.color = 'var(--color-danger)';
            if (data.error) {
              outputDiv.textContent += '\nError:\n' + data.error + '\n';
            }
            closeBtn.style.display = 'block';
          } else if (data.status === 'starting') {
            statusDiv.textContent = data.message;
          }
        } catch (e) {
          console.error('Failed to parse sync response:', line, e);
        }
      }
    }
  } catch (error) {
    statusDiv.textContent = '❌ Failed to start tile sync';
    statusDiv.style.color = 'var(--color-danger)';
    outputDiv.textContent = `Error: ${error.message}\n\nYou can manually run: npm run sync-tiles`;
    closeBtn.style.display = 'block';
  }
}

/**
 * Show Icon Attribution modal with credits for game-icons.net
 */
function showIconAttributionModal() {
  const modal = document.createElement('div');
  modal.className = 'icon-attribution-modal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${CONFIG.COLORS.OVERLAY_DARKER};
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: ${CONFIG.Z_INDEX_TOAST};
  `;

  modal.innerHTML = `
    <div style="
      background: var(--bg-surface);
      border: 1px solid var(--border-default);
      border-radius: var(--radius-lg);
      padding: var(--space-xl);
      max-width: 500px;
      max-height: 80vh;
      overflow-y: auto;
    ">
      <h2 style="margin-bottom: var(--space-md); color: var(--text-primary);">Icon Attribution</h2>
      <div style="color: var(--text-secondary); line-height: 1.6;">
        <p style="margin-bottom: var(--space-md);">
          This application includes icons from the following sources:
        </p>
        <div style="margin-bottom: var(--space-md);">
          <strong style="color: var(--text-primary);">Game Icons</strong><br>
          <a href="https://game-icons.net" target="_blank" rel="noopener" style="color: var(--color-primary);">game-icons.net</a><br>
          License: <a href="https://creativecommons.org/licenses/by/3.0/" target="_blank" rel="noopener" style="color: var(--color-primary);">CC BY 3.0</a><br>
          Various contributors
        </p>
        <div style="margin-bottom: var(--space-md);">
          <strong style="color: var(--text-primary);">RPG Awesome</strong><br>
          <a href="https://nagoshiashumari.github.io/Rpg-Awesome/" target="_blank" rel="noopener" style="color: var(--color-primary);">RPG Awesome</a><br>
          License: <a href="https://creativecommons.org/licenses/by/3.0/" target="_blank" rel="noopener" style="color: var(--color-primary);">CC BY 3.0</a><br>
          Various contributors
        </p>
        <p style="font-size: var(--text-sm); color: var(--text-disabled);">
          Icons are used under Creative Commons Attribution 3.0 Unported (CC BY 3.0) license.
          Modifications may have been made to fit the application's design.
        </p>
      </div>
      <button class="btn btn-primary" style="margin-top: var(--space-lg); width: 100%;" onclick="this.closest('.icon-attribution-modal').remove()">
        Close
      </button>
    </div>
  `;

  document.body.appendChild(modal);

  // Close on background click
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      modal.remove();
    }
  });

  // Close on Escape key
  const handleEscape = (e) => {
    if (e.key === 'Escape') {
      modal.remove();
      document.removeEventListener('keydown', handleEscape);
    }
  };
  document.addEventListener('keydown', handleEscape);
}

// Expose processMapImport for testing
window.processMapImport = processMapImport;
